import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import PageLayout, {
  PageSection,
} from "../../../components/navigation/PageLayout";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useTenantContext } from "../../../contexts/TenantContext";
import { useAuth } from "../../../contexts/AuthContext";
import { eligibilityCriteriaApi } from "../../../services/api";
import {
  getDefaultEligibilityRules,
  EligibilityReasonLabels,
} from "../../../utils/eligibilityCalculator";
import EligibilityTester from "./components/EligibilityTester";

/**
 * Format a date string to en-GB short format.
 */
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
  day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/**
 * Get tomorrow's date as YYYY-MM-DD for the minimum effective date.
 */
const getTomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

/**
 * Map a version DTO to the settings shape the EligibilityTester expects.
 */
function versionToSettings(v) {
  if (!v) {
    const defaults = getDefaultEligibilityRules();
    return {
   excludeProbation: defaults.excludeEmployeesInProbation,
    excludeManagement: defaults.excludeManagementRoles,
      minimumTenureDays: defaults.minimumServiceDays,
   minimumHoursPerWeek: defaults.minimumHoursInPeriod,
      includePartTime: true,
      includeCasual: true,
      probationPeriodDays: 90,
    };
  }
  return {
    excludeProbation: v.excludeEmployeesInProbation ?? false,
    excludeManagement: v.excludeManagementRoles ?? false,
    minimumTenureDays: v.minimumServiceDays ?? 0,
    minimumHoursPerWeek: v.minimumHoursInPeriod ?? 0,
 includePartTime: v.includePartTime ?? true,
    includeCasual: v.includeCasual ?? true,
    probationPeriodDays: 90,
  };
}

const EligibilityCalculationSettings = () => {
  const { selectedTenant, needsTenantSelection, isGlobalAdmin } =
    useTenantContext();
  const { isTenantAdmin, isTroncMaster } = useAuth();

  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(null); // version id being approved

  // Versioned data
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);

  // New version form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newVersion, setNewVersion] = useState({
    effectiveFrom: getTomorrow(),
    excludeEmployeesInProbation: false,
    excludeManagementRoles: false,
    requireMinimumServiceDays: false,
    minimumServiceDays: 0,
    requireMinimumHoursInPeriod: false,
    minimumHoursInPeriod: 0,
    requireEmployeeIsActive: true,
    requireEmployeeIsEligible: true,
    requireRoleIsEligible: true,
    requireRoleIsActive: true,
    requireEmploymentStarted: true,
    excludeTerminatedEmployees: true,
    includePartTime: true,
    includeCasual: true,
    changeNotes: "",
  });

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    {
      label: isGlobalAdmin ? "Tenant Settings" : "System Settings",
   path: "/system/eligibility",
    },
    { label: "Eligibility & Calculation", path: "/system/eligibility" },
  ];

  const canEdit = isGlobalAdmin || isTenantAdmin || isTroncMaster;

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Load versions
  const loadVersions = useCallback(async () => {
    setLoading(true);
    try {
      const [versionsRes, currentRes] = await Promise.all([
        eligibilityCriteriaApi.getVersions(),
   eligibilityCriteriaApi.getCurrent(),
      ]);

      if (versionsRes.success) {
        setVersions(versionsRes.data || []);
      }
      if (currentRes.success) {
  setCurrentVersion(currentRes.data || null);
      }
    } catch (err) {
      console.debug("Failed to load eligibility criteria versions:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isGlobalAdmin || selectedTenant) {
      loadVersions();
    } else {
      setLoading(false);
    }
  }, [isGlobalAdmin, selectedTenant?.id, loadVersions]);

  // Pre-fill new form from current version
  const handleStartNewVersion = () => {
    if (currentVersion) {
      setNewVersion({
        effectiveFrom: getTomorrow(),
        excludeEmployeesInProbation: currentVersion.excludeEmployeesInProbation,
        excludeManagementRoles: currentVersion.excludeManagementRoles,
        requireMinimumServiceDays: currentVersion.requireMinimumServiceDays,
        minimumServiceDays: currentVersion.minimumServiceDays,
   requireMinimumHoursInPeriod: currentVersion.requireMinimumHoursInPeriod,
      minimumHoursInPeriod: currentVersion.minimumHoursInPeriod,
        requireEmployeeIsActive: currentVersion.requireEmployeeIsActive,
   requireEmployeeIsEligible: currentVersion.requireEmployeeIsEligible,
        requireRoleIsEligible: currentVersion.requireRoleIsEligible,
     requireRoleIsActive: currentVersion.requireRoleIsActive,
        requireEmploymentStarted: currentVersion.requireEmploymentStarted,
        excludeTerminatedEmployees: currentVersion.excludeTerminatedEmployees,
        includePartTime: currentVersion.includePartTime,
        includeCasual: currentVersion.includeCasual,
    changeNotes: "",
      });
    } else {
      // First version: no effective date needed
setNewVersion({
      effectiveFrom: "",
        excludeEmployeesInProbation: false,
 excludeManagementRoles: false,
 requireMinimumServiceDays: false,
        minimumServiceDays: 0,
    requireMinimumHoursInPeriod: false,
   minimumHoursInPeriod: 0,
        requireEmployeeIsActive: true,
        requireEmployeeIsEligible: true,
        requireRoleIsEligible: true,
        requireRoleIsActive: true,
        requireEmploymentStarted: true,
        excludeTerminatedEmployees: true,
 includePartTime: true,
        includeCasual: true,
        changeNotes: "",
   });
    }
    setShowNewForm(true);
  };

  const handleNewVersionChange = (field, value) => {
    setNewVersion((prev) => ({ ...prev, [field]: value }));
  };

  // Check if this is the first version (no existing versions)
  const isFirstVersion = versions.length === 0;

  // Validate effective date is in the future (only required for subsequent versions)
  const isEffectiveDateValid = () => {
    if (isFirstVersion) return true; // First version doesn't need a date
    if (!newVersion.effectiveFrom) return false;
    const selected = new Date(newVersion.effectiveFrom);
    const today = new Date();
  today.setHours(0, 0, 0, 0);
    return selected > today;
  };

  // Save new version
  const handleSaveNewVersion = async () => {
    if (!isFirstVersion && !isEffectiveDateValid()) {
    showNotification("Effective date must be in the future.", "error");
      return;
 }

    setSaving(true);
    try {
      // Prepare the request data, formatting the date properly
      const requestData = {
     ...newVersion,
        // Remove milliseconds from ISO string: "2024-01-15T00:00:00.000Z" -> "2024-01-15T00:00:00Z"
      effectiveFrom: newVersion.effectiveFrom 
          ? new Date(newVersion.effectiveFrom + 'T00:00:00Z').toISOString().split('.')[0] + 'Z'
          : null,
 };

      const response = await eligibilityCriteriaApi.createVersion(requestData);
      if (response.success) {
        showNotification(
          `Version created (effective ${formatDate(newVersion.effectiveFrom)}). It requires approval before it takes effect.`
        );
        setShowNewForm(false);
        await loadVersions();
} else {
        showNotification(response.message || "Failed to create version", "error");
      }
    } catch (err) {
      showNotification(err.message || "Failed to create version", "error");
    } finally {
      setSaving(false);
    }
  };

  // Approve a pending version
  const handleApprove = async (versionId) => {
    setApproving(versionId);
    try {
   const response = await eligibilityCriteriaApi.approveVersion(versionId);
      if (response.success) {
    showNotification("Version approved. It will take effect on its effective date.");
        await loadVersions();
      } else {
        showNotification(response.message || "Failed to approve version", "error");
      }
    } catch (err) {
      showNotification(err.message || "Failed to approve version", "error");
    } finally {
   setApproving(null);
    }
  };

  // Derive settings for the EligibilityTester from either the new form (if open) or current version
  const testerSettings = showNewForm
 ? {
        excludeProbation: newVersion.excludeEmployeesInProbation,
        excludeManagement: newVersion.excludeManagementRoles,
    minimumTenureDays: newVersion.minimumServiceDays,
        minimumHoursPerWeek: newVersion.minimumHoursInPeriod,
 includePartTime: newVersion.includePartTime,
   includeCasual: newVersion.includeCasual,
        probationPeriodDays: 90,
      }
: versionToSettings(currentVersion);

  // Select Tenant Empty State for Global Admins
  if (isGlobalAdmin && needsTenantSelection) {
    return (
      <>
     <Helmet>
    <title>Eligibility & Calculation - Rocket Tronc</title>
    </Helmet>
   <PageLayout
          title="Eligibility & Calculation Settings"
          description="Configure TRONC eligibility rules and calculation methods."
     breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
        <Icon name="Building2" size={40} className="text-primary" />
     </div>
   <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              As a Global Administrator, please select a tenant from the sidebar
              to configure their eligibility and calculation settings.
            </p>
      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
   <Icon name="ArrowLeft" size={16} />
      <span>Use the tenant selector in the sidebar</span>
      </div>
 </div>
    </PageLayout>
      </>
    );
  }

  // Separate versions by status
  const pendingVersions = versions.filter((v) => !v.isApproved);
  const scheduledVersions = versions.filter((v) => v.isScheduled);
  const historicalVersions = versions.filter(
    (v) => v.isApproved && !v.isCurrentlyActive && !v.isScheduled
  );

  return (
    <>
      <Helmet>
  <title>
  Eligibility & Calculation - {selectedTenant?.name || "Rocket Tronc"}
        </title>
      </Helmet>

      <PageLayout
        title="Eligibility & Calculation Settings"
      description={
          selectedTenant
         ? `Configure ${selectedTenant.name}'s TRONC eligibility rules.`
            : "Configure TRONC eligibility rules."
        }
        breadcrumbs={breadcrumbs}
        actions={
          canEdit && !showNewForm ? (
       <Button
     variant="default"
      iconName="Plus"
       iconPosition="left"
       onClick={handleStartNewVersion}
    >
            New Version
       </Button>
          ) : null
   }
      >
        {/* Notification Toast */}
        {notification && (
          <div
          className={`fixed top-20 right-4 z-[1300] alert ${
              notification.type === "error" ? "alert-error" : "alert-success"
  } shadow-lg`}
      >
            <Icon
    name={notification.type === "error" ? "AlertCircle" : "CheckCircle"}
           size={20}
            />
          <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-2">
     <Icon name="X" size={16} />
         </button>
          </div>
        )}

        {/* Compliance Banner */}
 {!loading && canEdit && (
          <div className="mb-6 bg-info/5 border border-info/20 rounded-lg p-4 flex items-start gap-3">
         <Icon name="ShieldCheck" size={20} className="text-info mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-info mb-1">
          HMRC Compliance: Eligibility Changes Are Versioned
       </p>
    <p className="text-muted-foreground">
       Eligibility criteria are immutable once active. To change them, create a new version
 with a future effective date. The current active version cannot be edited.
   All employees must have a linked job role for eligibility rules to apply correctly.
          </p>
  </div>
      </div>
        )}

        {loading ? (
     <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Left side: Versions & Form (2 columns on xl) */}
            <div className="xl:col-span-2 space-y-6">
  {/* ============ NEW VERSION FORM ============ */}
    {showNewForm && (
        <div className="card border-2 border-primary/30">
        <div className="card-header bg-primary/5">
         <div className="flex items-center justify-between">
     <div className="flex items-center gap-3">
       <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Plus" size={20} className="text-primary" />
       </div>
    <div>
        <h3 className="font-semibold">Create New Eligibility Version</h3>
              <p className="text-xs text-muted-foreground">
          Changes will take effect from the date you specify
 </p>
       </div>
  </div>
           <button
   onClick={() => setShowNewForm(false)}
         className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
           <Icon name="X" size={18} />
             </button>
    </div>
      </div>
    <div className="card-content space-y-6 p-6">
         {/* Effective Date - REQUIRED for subsequent versions, NOT shown for first */}
      {isFirstVersion ? (
    <div className="p-4 bg-success/5 border border-success/20 rounded-lg">
             <div className="flex items-center gap-2 mb-1">
  <Icon name="Zap" size={16} className="text-success" />
               <span className="font-medium text-success">First Version</span>
             </div>
     <p className="text-xs text-muted-foreground">
 This is your first eligibility criteria version. It will be activated immediately with today's date. No effective date is required.
     </p>
    </div>
         ) : (
<div className="p-4 bg-warning/5 border border-warning/20 rounded-lg">
      <label className="label flex items-center gap-2 mb-2">
    <Icon name="Calendar" size={16} className="text-warning" />
         Effective From Date <span className="text-error">*</span>
        </label>
     <input
      type="date"
value={newVersion.effectiveFrom}
 onChange={(e) =>
       handleNewVersionChange("effectiveFrom", e.target.value)
    }
 min={getTomorrow()}
   className="input w-full max-w-xs"
   />
    <p className="text-xs text-muted-foreground mt-1">
       Must be a future date. The current active version will expire the day before.
    </p>
  {newVersion.effectiveFrom && !isEffectiveDateValid() && (
         <p className="text-xs text-error mt-1 flex items-center gap-1">
   <Icon name="AlertCircle" size={12} />
    Effective date must be in the future
       </p>
      )}
   </div>
         )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Employee Requirements */}
            <div className="space-y-4">
   <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
   Employee Requirements
             </h4>

           {/* Minimum Hours */}
         <div>
 <label className="label">Minimum Shift Time</label>
       <div className="flex items-center gap-2">
     <input
   type="number"
 value={newVersion.minimumHoursInPeriod}
   onChange={(e) => {
    const val = parseInt(e.target.value) || 0;
        handleNewVersionChange("minimumHoursInPeriod", val);
         handleNewVersionChange("requireMinimumHoursInPeriod", val > 0);
       }}
       className="input w-24 text-right"
  min="0"
           max="24"
         />
        <span className="text-muted-foreground">hours per shift</span>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
      Minimum hours per shift to be eligible. When no clock records exist, contracted hours ÷ 5 is used as the daily average.
      </p>
     </div>

   {/* Minimum Tenure */}
          <div>
  <label className="label">Minimum Tenure</label>
    <div className="flex items-center gap-2">
   <input
         type="number"
   value={newVersion.minimumServiceDays}
       onChange={(e) => {
       const val = parseInt(e.target.value) || 0;
       handleNewVersionChange("minimumServiceDays", val);
 handleNewVersionChange("requireMinimumServiceDays", val > 0);
}}
className="input w-24 text-right"
           min="0"
           />
            <span className="text-muted-foreground">days</span>
        </div>
          </div>

    {/* Exclude Probation */}
      <ToggleSetting
   label="Exclude Probation Period"
    description="Staff on probation are not eligible"
 checked={newVersion.excludeEmployeesInProbation}
          onChange={(v) =>
   handleNewVersionChange("excludeEmployeesInProbation", v)
      }
   />

   {/* Exclude Management */}
       <ToggleSetting
   label="Exclude Management"
             description="Management roles are not eligible"
       checked={newVersion.excludeManagementRoles}
       onChange={(v) =>
 handleNewVersionChange("excludeManagementRoles", v)
   }
   />
               </div>

         {/* Employment Type */}
    <div className="space-y-4">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
     Employment Type
        </h4>

      {/* Include Part-Time */}
   <ToggleSetting
  label="Include Part-Time Staff"
       description="Part-time employees are eligible for TRONC"
         checked={newVersion.includePartTime}
               onChange={(v) =>
     handleNewVersionChange("includePartTime", v)
        }
       />

         {/* Include Casual */}
    <ToggleSetting
             label="Include Casual Staff"
         description="Casual/zero-hours staff are eligible"
               checked={newVersion.includeCasual}
             onChange={(v) =>
           handleNewVersionChange("includeCasual", v)
   }
    />

{/* Require Role is Eligible */}
            <ToggleSetting
               label="Require Job Role Eligibility"
      description="Employee's linked job role must be marked as TRONC-eligible"
   checked={newVersion.requireRoleIsEligible}
         onChange={(v) =>
     handleNewVersionChange("requireRoleIsEligible", v)
        }
      />

         {/* Require Role is Active */}
      <ToggleSetting
             label="Require Active Job Role"
   description="Employee's linked job role must be active"
      checked={newVersion.requireRoleIsActive}
      onChange={(v) =>
      handleNewVersionChange("requireRoleIsActive", v)
         }
          />
           </div>
 </div>

      {/* Change Notes */}
<div>
          <label className="label">Change Notes</label>
              <textarea
          value={newVersion.changeNotes}
  onChange={(e) =>
    handleNewVersionChange("changeNotes", e.target.value)
       }
               placeholder="Describe what changed and why (for audit trail)..."
    className="input w-full"
      rows="3"
              />
          </div>

            {/* Action Buttons */}
<div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button
           variant="outline"
  onClick={() => setShowNewForm(false)}
                disabled={saving}
 >
      Cancel
             </Button>
 <Button
    variant="default"
     iconName="Save"
         iconPosition="left"
             onClick={handleSaveNewVersion}
disabled={saving || (!isFirstVersion && !isEffectiveDateValid())}
    loading={saving}
      >
         {saving ? "Creating..." : isFirstVersion ? "Create & Activate" : "Create Version"}
    </Button>
            </div>
     </div>
    </div>
              )}

   {/* ============ CURRENT ACTIVE VERSION ============ */}
    {currentVersion ? (
     <div className="card border-2 border-success/30">
       <div className="card-header bg-success/5">
          <div className="flex items-center justify-between">
             <div className="flex items-center gap-3">
 <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
           <Icon name="ShieldCheck" size={20} className="text-success" />
     </div>
  <div>
   <h3 className="font-semibold flex items-center gap-2">
      Current Active Rules
      <span className="badge badge-success text-xs">
             v{currentVersion.version}
    </span>
         </h3>
   <p className="text-xs text-muted-foreground">
 Effective since {formatDate(currentVersion.effectiveFrom)}
           {currentVersion.approvedBy &&
       ` · Approved by ${currentVersion.approvedBy}`}
 </p>
         </div>
   </div>
        <Icon name="Lock" size={16} className="text-muted-foreground" />
       </div>
     </div>
             <div className="card-content p-6">
   <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
               <RuleBadge
      label="Exclude Probation"
  enabled={currentVersion.excludeEmployeesInProbation}
      />
                <RuleBadge
           label="Exclude Management"
        enabled={currentVersion.excludeManagementRoles}
             />
                <RuleBadge
       label="Min Service Days"
    enabled={currentVersion.requireMinimumServiceDays}
               value={
        currentVersion.requireMinimumServiceDays
   ? `${currentVersion.minimumServiceDays}d`
          : null
            }
                 />
      <RuleBadge
                label="Min Shift Time"
             enabled={currentVersion.requireMinimumHoursInPeriod}
            value={
              currentVersion.requireMinimumHoursInPeriod
  ? `${currentVersion.minimumHoursInPeriod}h`
   : null
     }
    />
          <RuleBadge
label="Part-Time"
           enabled={currentVersion.includePartTime}
               />
         <RuleBadge
     label="Casual Staff"
            enabled={currentVersion.includeCasual}
                   />
           <RuleBadge
               label="Role Eligibility"
            enabled={currentVersion.requireRoleIsEligible}
            />
          <RuleBadge
      label="Active Role"
             enabled={currentVersion.requireRoleIsActive}
         />
      </div>
         {currentVersion.changeNotes && (
   <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
 <span className="font-medium">Notes:</span>{" "}
     {currentVersion.changeNotes}
  </div>
 )}
        </div>
         </div>
              ) : (
    <div className="card p-8 text-center">
      <Icon
     name="Shield"
         size={40}
         className="mx-auto mb-3 text-muted-foreground"
        />
       <h3 className="font-semibold mb-2">No Active Eligibility Rules</h3>
     <p className="text-muted-foreground text-sm mb-4">
             System defaults are being used. Create and approve a version to set custom rules.
        </p>
 {canEdit && (
 <Button
      variant="default"
           iconName="Plus"
          iconPosition="left"
      onClick={handleStartNewVersion}
        >
           Create First Version
         </Button>
          )}
             </div>
         )}

        {/* ============ PENDING VERSIONS ============ */}
     {pendingVersions.length > 0 && (
                <PageSection title="Pending Approval">
                <div className="space-y-3">
           {pendingVersions.map((v) => (
       <div
      key={v.id}
  className="card border border-warning/30 bg-warning/5"
    >
   <div className="p-4 flex items-center justify-between">
         <div className="flex items-center gap-3">
           <div className="w-8 h-8 rounded-full bg-warning/10 flex items-center justify-center">
    <Icon name="Clock" size={16} className="text-warning" />
 </div>
         <div>
          <p className="font-medium text-sm">
        Version {v.version}{" "}
            <span className="badge badge-warning text-xs ml-1">
     Pending
    </span>
    </p>
 <p className="text-xs text-muted-foreground">
    Effective from {formatDate(v.effectiveFrom)}
   {v.createdBy && ` · Created by ${v.createdBy}`}
           </p>
        {v.changeNotes && (
             <p className="text-xs text-muted-foreground mt-1 italic">
          {v.changeNotes}
           </p>
  )}
             </div>
           </div>
     {canEdit && (
            <Button
        variant="default"
         size="sm"
  iconName="Check"
            iconPosition="left"
       onClick={() => handleApprove(v.id)}
    loading={approving === v.id}
      disabled={approving !== null}
        >
      Approve
       </Button>
               )}
          </div>
       <div className="px-4 pb-4">
        <VersionRuleSummary version={v} />
             </div>
  </div>
    ))}
     </div>
          </PageSection>
              )}

     {/* ============ SCHEDULED VERSIONS ============ */}
              {scheduledVersions.length > 0 && (
        <PageSection title="Scheduled">
                  <div className="space-y-3">
      {scheduledVersions.map((v) => (
     <div
     key={v.id}
        className="card border border-info/30 bg-info/5"
           >
          <div className="p-4">
         <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-info/10 flex items-center justify-center">
   <Icon name="CalendarClock" size={16} className="text-info" />
       </div>
       <div>
   <p className="font-medium text-sm">
       Version {v.version}{" "}
             <span className="badge badge-info text-xs ml-1">
                Scheduled
        </span>
      </p>
 <p className="text-xs text-muted-foreground">
        Takes effect {formatDate(v.effectiveFrom)}
         {v.approvedBy && ` · Approved by ${v.approvedBy}`}
      </p>
       </div>
                  </div>
              <VersionRuleSummary version={v} />
              </div>
         </div>
              ))}
  </div>
     </PageSection>
        )}

     {/* ============ VERSION HISTORY ============ */}
          {historicalVersions.length > 0 && (
       <PageSection title="Version History">
<div className="card overflow-hidden">
         <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold">
       Version
        </th>
         <th className="px-4 py-3 text-left text-xs font-semibold">
     Effective Period
       </th>
          <th className="px-4 py-3 text-left text-xs font-semibold">
        Approved By
                </th>
        <th className="px-4 py-3 text-left text-xs font-semibold">
    Notes
</th>
      </tr>
         </thead>
   <tbody className="divide-y divide-border">
          {historicalVersions.map((v) => (
        <tr key={v.id} className="hover:bg-muted/30">
   <td className="px-4 py-3 text-sm font-medium">
 v{v.version}
          </td>
         <td className="px-4 py-3 text-sm text-muted-foreground">
  {formatDate(v.effectiveFrom)} –{" "}
  {formatDate(v.effectiveTo)}
       </td>
       <td className="px-4 py-3 text-sm text-muted-foreground">
         {v.approvedBy || "—"}
        </td>
          <td className="px-4 py-3 text-sm text-muted-foreground truncate max-w-[200px]">
       {v.changeNotes || "—"}
     </td>
        </tr>
        ))}
             </tbody>
    </table>
    </div>
   </div>
   </PageSection>
     )}
            </div>

       {/* Right side: Eligibility Tester (1 column on xl, sticky) */}
            <div className="xl:col-span-1">
      <div className="sticky top-24">
         <EligibilityTester settings={testerSettings} />
         </div>
            </div>
       </div>
    )}
      </PageLayout>
    </>
  );
};

/**
 * Toggle switch for a boolean setting.
 */
const ToggleSetting = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
    <div>
  <label className="font-medium">{label}</label>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
    </div>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
     type="checkbox"
     checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
    </label>
  </div>
);

/**
 * Read-only badge showing if a rule is enabled/disabled.
 */
const RuleBadge = ({ label, enabled, value }) => (
  <div
    className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
      enabled
     ? "bg-success/10 text-success border border-success/20"
 : "bg-muted text-muted-foreground border border-border"
    }`}
  >
    <Icon name={enabled ? "Check" : "X"} size={14} />
    <span>
      {label}
      {value && <span className="font-semibold ml-1">{value}</span>}
    </span>
  </div>
);

/**
 * Compact summary of a version's rules.
 */
const VersionRuleSummary = ({ version: v }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
    <div className={`flex items-center gap-1 ${v.excludeEmployeesInProbation ? "text-success" : "text-muted-foreground"}`}>
      <Icon name={v.excludeEmployeesInProbation ? "Check" : "X"} size={10} />
      Excl. Probation
</div>
    <div className={`flex items-center gap-1 ${v.excludeManagementRoles ? "text-success" : "text-muted-foreground"}`}>
      <Icon name={v.excludeManagementRoles ? "Check" : "X"} size={10} />
      Excl. Mgmt
    </div>
    <div className={`flex items-center gap-1 ${v.requireMinimumServiceDays ? "text-success" : "text-muted-foreground"}`}>
      <Icon name={v.requireMinimumServiceDays ? "Check" : "X"} size={10} />
      Min {v.minimumServiceDays}d tenure
 </div>
    <div className={`flex items-center gap-1 ${v.requireMinimumHoursInPeriod ? "text-success" : "text-muted-foreground"}`}>
      <Icon name={v.requireMinimumHoursInPeriod ? "Check" : "X"} size={10} />
   Min {v.minimumHoursInPeriod}h/shift
    </div>
  </div>
);

export default EligibilityCalculationSettings;
